/**
 * Content API for Native Apps
 *
 * Primary: Fetches content from the Content API (Neon Postgres).
 * Fallback: CDN static manifests (legacy, for offline/API outage).
 *
 * Uses in-memory cache to avoid redundant network requests within a session.
 */

const API_BASE = 'https://theartofcto.com/api/public/content';
const CDN_BASE = 'https://theartofcto.com/content';

export interface ContentEntry {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  tags: string[];
  author: string;
  category: string;
  featured?: boolean;
  premium?: boolean;
  image?: string;
}

export interface ContentManifest {
  category: string;
  count: number;
  lastUpdated: string;
  posts: ContentEntry[];
}

// In-memory cache (per session)
const cache = new Map<string, { data: ContentManifest; fetchedAt: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const STORAGE_PREFIX = 'content_manifest_';

/** Persist manifest to localStorage for offline/cold-start access. */
function persistToStorage(category: string, data: ContentManifest): void {
  try {
    localStorage.setItem(
      STORAGE_PREFIX + category,
      JSON.stringify({ data, savedAt: Date.now() })
    );
  } catch {
    // Storage full or unavailable
  }
}

/** Load manifest from localStorage (offline fallback). */
function loadFromStorage(category: string): ContentManifest | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + category);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Try fetching from the Content API (database-backed).
 * Returns null on failure so caller can fall back.
 */
async function fetchFromApi(category: string): Promise<ContentManifest | null> {
  try {
    const response = await fetch(`${API_BASE}/list/${category}?pageSize=100`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;

    const data = await response.json() as ContentManifest;
    return data;
  } catch {
    return null;
  }
}

/**
 * Try fetching from the CDN static manifest (legacy fallback).
 * Returns null on failure.
 */
async function fetchFromCdn(category: string): Promise<ContentManifest | null> {
  try {
    const response = await fetch(`${CDN_BASE}/${category}.json`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;

    return await response.json() as ContentManifest;
  } catch {
    return null;
  }
}

/**
 * Fetch a category's content manifest.
 * Priority: memory cache → Content API → CDN → localStorage → null
 */
export async function fetchManifest(
  category: string
): Promise<ContentManifest | null> {
  const cached = cache.get(category);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
    return cached.data;
  }

  // Try Content API first (real-time, database-backed)
  let data = await fetchFromApi(category);

  // Fall back to CDN static manifests
  if (!data) {
    data = await fetchFromCdn(category);
  }

  if (data) {
    cache.set(category, { data, fetchedAt: Date.now() });
    persistToStorage(category, data);
    return data;
  }

  // Network failure — return stale memory cache, then localStorage, then null
  return cached?.data ?? loadFromStorage(category);
}

/**
 * Fetch the "recent" manifest — last 30 days of content across all categories.
 */
export async function fetchRecentContent(): Promise<ContentEntry[]> {
  // Try Content API recent endpoint first
  try {
    const response = await fetch(`${API_BASE}/recent?days=30&limit=50`, {
      headers: { Accept: 'application/json' },
    });
    if (response.ok) {
      const data = await response.json() as { posts: ContentEntry[] };
      return data.posts ?? [];
    }
  } catch {
    // Fall through to legacy approach
  }

  // Legacy fallback
  const manifest = await fetchManifest('recent');
  return manifest?.posts ?? [];
}

/**
 * Fetch a specific category's posts.
 */
export async function fetchCategoryPosts(
  category: string
): Promise<ContentEntry[]> {
  const manifest = await fetchManifest(category);
  return manifest?.posts ?? [];
}

/**
 * Fetch today's Daily Sync entry (if published).
 */
export async function fetchTodaysDailySync(): Promise<ContentEntry | null> {
  const posts = await fetchCategoryPosts('daily-sync');
  if (posts.length === 0) return null;

  const today = new Date().toISOString().split('T')[0];
  // Check for today's post first
  const todayPost = posts.find((p) => p.date === today || p.slug.includes(today));
  // Fall back to the most recent
  return todayPost ?? posts[0];
}

/**
 * Clear both in-memory and localStorage caches (e.g., on pull-to-refresh).
 */
export function clearContentCache(): void {
  cache.clear();
  try {
    const keys = Object.keys(localStorage).filter((k) =>
      k.startsWith(STORAGE_PREFIX)
    );
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage unavailable
  }
}
